import React, {useState, useEffect} from 'react'
import Select from './UI/Select.js'
import {AddressSearch, FormRow} from '../../src/components/UI/forms/index.js'
import compareAddresses from '../../utils/compareAddresses.js'
import {Address, OrderAddress, Reseller, Route, User} from '../../types/types.js'
import _ from 'lodash'

interface ChooseRoutePointProps {
    defaultOption?: string
    address?: OrderAddress
    reseller: Reseller
    user?: User
    route: Route
    label: string
    disabled?: boolean
    readOnly?: boolean
    onChange: (address: Address) => void
}

const ChooseRoutePoint: React.FC<ChooseRoutePointProps> = ({
    defaultOption = 'companyLocation',
    address,
    reseller,
    user,
    route,
    label,
    disabled,
    onChange
}) => {
    const [addressOption, setAddressOption] = useState<string>(defaultOption)
    const [addressField, setAddressField] = useState<Address | undefined>(address)
    const [positionError, setPositionError] = useState<boolean>(false)

    useEffect(() => {
        if (compareAddresses(address, user?.address)) {
            setAddressOption('userAddress')
        } else {
            let matched = false

            if (reseller?.settings?.branches?.length > 0) {
                matched = _.some(reseller.settings.branches, (branch, index) => {
                    if (compareAddresses(address, branch.address)) {
                        setAddressOption(`branch-${index}`)
                        return true
                    }
                    return false
                })
            } else if (compareAddresses(address, reseller?.settings?.companyAddress)) {
                setAddressOption('companyLocation')
                matched = true
            } else {
                matched = _.some(reseller?.settings?.sublocations, (sublocation, index) => {
                    if (compareAddresses(address, sublocation)) {
                        setAddressOption(`sublocation-${index}`)
                        return true
                    }
                    return false
                })
            }

            if (!matched) {
                _.some(route?.addresses, (routeAddress, index) => {
                    if (compareAddresses(address, routeAddress)) {
                        setAddressOption(`routeAddress-${index}`)
                        return true
                    }
                    return false
                })
            }
        }

        if (!address) {
            const initialOption = reseller?.settings?.branches?.length > 0 ? 'branch-0' : defaultOption

            setAddressOption(initialOption)
            handleAddressOptionChange({target: {value: initialOption}} as React.ChangeEvent<HTMLInputElement>)
        }
    }, [address, defaultOption, reseller, route, user])

    const handleConfirm = (address: Address) => {
        if (address && address.position) {
            // const {_id, ...addressWithoutId} = address // Remove `id` if it exists
            onChange(address)
        } else {
            setPositionError(true)
        }
    }

    const getCurrentPosition = async (): Promise<{lat: number, lng: number, accuracy: number}> => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) =>
                        resolve({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        }),
                    reject,
                    {enableHighAccuracy: true, timeout: 1000}
                )
            } else {
                reject(new Error('Geolocation not supported'))
            }
        })
    }

    const handleAddressOptionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const option = event.target.value
        let newAddress: Address | undefined

        if (option.includes('routeAddress')) {
            const addressIndex = parseInt(option.split('-')[1], 10)
            newAddress = route.addresses?.[addressIndex]
        } else if (option === 'address') {
            setAddressField({})
        } else if (option === 'companyLocation') {
            newAddress = reseller.settings.companyAddress
        } else if (option.includes('sublocation')) {
            const addressIndex = parseInt(option.split('-')[1], 10)
            newAddress = reseller.settings.sublocations?.[addressIndex]
        } else if (option.includes('branch')) {
            const addressIndex = parseInt(option.split('-')[1], 10)
            newAddress = reseller.settings.branches?.[addressIndex]?.address
        } else if (option === 'userAddress') {
            newAddress = user?.address
        } else if (option === 'currentLocation') {
            const position = await getCurrentPosition()
            console.log(position)
            newAddress = {
                name: 'Huidige locatie',
                street: '',
                street2: '',
                nr: '',
                postalCode: '',
                city: '',
                country: '',
                position
            }
        }

        if (option !== 'address') {
            setAddressField(newAddress)
            handleConfirm(newAddress!)
        }

        setAddressOption(option)
    }

    const handleAddressFieldChange = (newAddress: Address) => {
        setAddressField(newAddress)
        handleConfirm(newAddress)
    }

    const addressOptions = [
        ...(reseller.settings.branches?.length > 0 ?
            reseller.settings.branches.map((branch, index) => ({
                title: branch.name,
                value: `branch-${index}`,
                bold: true
            })) :
            [
                {title: 'Bedrijfslocatie', value: 'companyLocation', bold: true},
                ...(reseller.settings.sublocations?.map((address, index) => ({
                    title: address.name,
                    value: `sublocation-${index}`,
                    bold: true
                })) || [])
            ]),
        {title: 'Huidige locatie', value: 'currentLocation', bold: true},

        ...user?.address.position ?
            [
                {title: 'Mijn adres', value: 'userAddress', bold: true}
            ] :
            [],
        {title: 'Anders:', value: 'address', bold: true},
        ...(route.addresses?.map((address, index) => ({
            title: `${address.street} ${address.nr}${address.addition || ''}, ${address.city}`,
            value: `routeAddress-${index}`
        })) || [])
    ]

    return (
        <FormRow>
            <Select
                label={label}
                value={addressOption}
                onChange={handleAddressOptionChange}
                options={addressOptions}
                disabled={disabled}
                info={
                    positionError &&
                    'Dit adres staat niet in onze database, controleer of dit adres geldig is.'
                }
                infoIcon='mdi mdi-alert'
            />

            <AddressSearch
                address={addressField}
                disabled={addressOption !== 'address'}
                onChange={handleAddressFieldChange}
            />
        </FormRow>
    )
}

export default ChooseRoutePoint
